import React, { useState, useEffect } from "react";
import { Button, Loading, Switch, Modal, Text, Input } from "@nextui-org/react";
import { useSelector } from "react-redux";
import { PaperPlus } from "react-iconly";

import { secureFetch } from "../../lib/api";
import { success, warning, error } from "../../lib/userAlerts";
import { mstore } from "../../store";
import endpoint from "../../config/endpoint.json";

const SafetyDocumentType = () => {
  const [state, setState] = useState({
    documentTypeList: [],
    documentTypeListc: [],
    addDocument: false,
    editDocumentType: false,
    deleteDocuments: false,
    loading: false,
    loadinge: false,
    addDocumentsName: "",
    newArtifactsName: "",
    oldArtifactsName: "",
    deleteDocumentsName: "",
    fetching: true,
    editButtonClicked: false,
    editIndex: null,
    togglingId: null,
  });

  const Access = useSelector(state => state.navigation.Access);
  const clientId = useSelector(state => 
    state.client?.id || mstore.getPath(["user", "company_id"])
  );

  const getDocumentTypeList = async () => {
    try {
      const [ok, , json] = await secureFetch(endpoint.getDocumentTypeList, {});
      if (ok) {
        setState(prev => ({
          ...prev,
          documentTypeList: json.documentTypeList,
          loading: false,
          fetching: false,
          documentTypeListc: JSON.parse(JSON.stringify(json.documentTypeList)),
        }));
      } else {
        error("Oops! error occurred during fetching safety document type list!");
      }
    } catch (err) {
      error("Oops! error occurred during fetching safety document type list!");
    }
  };

  useEffect(() => {
    getDocumentTypeList();
  }, [clientId]);

  const handleCreateSafetyType = () => {
    if (!state.addDocumentsName) {
      warning("Please enter name of artifact");
      return;
    }

    if (state.documentTypeList.some(item => item.type_name === state.addDocumentsName.trim())) {
      warning("This artifact type already exists");
      return;
    }

    addDocumentTypes();
  };

  const handleSwitch = async (obj) => {
    setState(prev => ({ ...prev, togglingId: obj.id }));
    try {
      if (obj.status === 1) {
        await deleteDocuments(obj);
      } else {
        await recoverDocumentTypes(obj);
      }
    } finally {
      setState(prev => ({ ...prev, togglingId: null }));
    }
  };

  const handleAutoCrop = async (obj) => {
    setState(prev => ({ ...prev, togglingId: `crop-${obj.id}` }));
    try {
      if (obj.is_scanner_enable === 1) {
        await disableAutoCrop(obj);
      } else {
        await enableAutoCrop(obj);
      }
    } finally {
      setState(prev => ({ ...prev, togglingId: null }));
    }
  };

  const disableAutoCrop = async (obj) => {
    try {
      const [ok, , json] = await secureFetch(endpoint.disableAutoCropForSafetyDocType, {
        type_name: obj.type_name,
      });
      
      if (ok) {
        await getDocumentTypeList();
        success(json.message);
        setState(prev => ({ ...prev, loading: false }));
      } else {
        error("Oops! error occurred during disable auto crop features!");
      }
    } catch (err) {
      error("Oops! error occurred during disable auto crop features!");
    }
  };

  const enableAutoCrop = async (obj) => {
    try {
      const [ok, , json] = await secureFetch(endpoint.enableAutoCropForSafetyDocType, {
        type_name: obj.type_name,
      });
      
      if (ok) {
        await getDocumentTypeList();
        setState(prev => ({ ...prev, loading: false }));
      } else {
        error("Oops! error occurred during enable auto crop features!");
      }
    } catch (err) {
      error("Oops! error occurred during enable auto crop features!");
    }
  };

  const addDocumentTypes = async () => {
    setState(prev => ({ ...prev, loading: true }));
    try {
      const [ok, , json] = await secureFetch(endpoint.addNewDocumentType, {
        type_name: state.addDocumentsName,
      });
      
      if (ok) {
        await getDocumentTypeList();
        setState(prev => ({ 
          ...prev, 
          loading: false, 
          addDocumentsName: "", 
          addDocument: false 
        }));
      } else {
        error("Oops! error occurred during add new safety document type!");
      }
    } catch (err) {
      error("Oops! error occurred during add new safety document type!");
    }
  };

  const editDocumentType = (obj) => {
    if (obj.status !== 1) return;
    setState(prev => ({ 
      ...prev, 
      editButtonClicked: true, 
      editIndex: obj.id 
    }));
  };

  const saveDocumentType = async (obj) => {
    if (obj.status !== 1) return;

    if (state.documentTypeListc.some(item => item.type_name === obj.type_name.trim())) {
      warning("This artifact type already exists");
      setState(prev => ({ ...prev, editButtonClicked: false }));
      return;
    }

    setState(prev => ({ ...prev, loadinge: true }));
    try {
      const [ok, , json] = await secureFetch(endpoint.editDocumentType, {
        new_type_name: obj.type_name,
        id: obj.id,
      });
      
      if (ok) {
        await getDocumentTypeList();
        success(json.message);
        setState(prev => ({ ...prev, editButtonClicked: false }));
      } else {
        error("Oops! error occurred during save safety document type!");
      }
    } catch (err) {
      error("Oops! error occurred during save safety document type!");
    }
  };

  const deleteDocuments = async (obj) => {
    if (state.editButtonClicked) {
      warning("Please save first");
      return;
    }

    setState(prev => ({ ...prev, loading: true }));
    try {
      const [ok, , json] = await secureFetch(endpoint.deleteDocumentType, {
        type_name: obj.type_name,
      });
      
      if (ok) {
        await getDocumentTypeList();
        success(json.message);
        setState(prev => ({
          ...prev,
          loading: false,
          deleteDocumentsName: "",
          isActive: false,
        }));
      } else {
        error("Oops! error occurred during delete safety document type!");
      }
    } catch (err) {
      error("Oops! error occurred during delete safety document type!");
    }
  };

  const recoverDocumentTypes = async (obj) => {
    setState(prev => ({ ...prev, loading: true }));
    try {
      const [ok, , json] = await secureFetch(endpoint.recoverDocumentType, {
        type_name: obj.type_name,
      });
      
      if (ok) {
        await getDocumentTypeList();
        success(json.message);
        setState(prev => ({ ...prev, loading: false, isActive: true }));
      } else {
        error("Oops! error occurred during recover safety document type!");
      }
    } catch (err) {
      error("Oops! error occurred during recover safety document type!");
    }
  };

  const renderUserRow = (obj, idx) => (
    <tr key={idx} className="flex w-100 hover:bg-gray-50 transition-colors duration-200">
      <td className="f5 fw2 flex flex-start items-center h3 w-25 p-4">
        <div className="font-medium text-gray-700">{obj.type_name}</div>
      </td>
      <td className="f5 flex justify-center items-center h3 w-25 p-4">
        {state.togglingId === obj.id ? (
          <div className="flex items-center justify-center">
            <Loading size="sm" color="primary" />
          </div>
        ) : (
          <Switch
            size='sm'
            checked={obj.status === 1}
            onChange={() => handleSwitch(obj)}
            className="hover:opacity-80 transition-opacity"
          />
        )}
      </td>
      <td className="flex justify-center items-center h3 w-25 p-4">
        {state.togglingId === `crop-${obj.id}` ? (
          <div className="flex items-center justify-center">
            <Loading size="sm" color="primary" />
          </div>
        ) : (
          <Switch
            size='sm'
            disabled={obj.status === 0}
            checked={obj.is_scanner_enable === 1}
            onChange={() => handleAutoCrop(obj)}
            className={`hover:opacity-80 transition-opacity ${obj.status === 0 ? 'opacity-50' : ''}`}
          />
        )}
      </td>
    </tr>
  );

  const renderNewSafetyTypeModal = () => (
    <Modal
      open={state.addDocument}
      blur
      css={{br: 0}}
      onClose={() => setState(prev => ({ ...prev, addDocument: false }))}
      className="max-w-2xl"
    >
      <Modal.Header>
        <Text size={20} className="font-semibold">Add Safety Document Type</Text>
      </Modal.Header>
      <Modal.Body>
        <label htmlFor="" className="text-gray-700 font-medium mb-2 block">
          Document Type Name
        </label>
        <div>
          <Input
            type="text"
            placeholder="Write New Document Type That You Want To Add"
            clearable
            onChange={(e) => setState(prev => ({ ...prev, addDocumentsName: e.target.value }))}
            css={{ $$inputBorderRadius: "0", w: '100%', h: 40, mt: 2 }}
            shadow={false}
            bordered={true}
            rounded={false}
            value={state.addDocumentsName}
            className="hover:border-blue-500 transition-colors"
          />
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button
          css={{br: 0}}
          auto
          color="primary"
          className="hover:bg-blue-600 transition-colors"
          onClick={handleCreateSafetyType}
        >
          {state.loading ? (
            <div className="flex items-center space-x-2">
              <Loading size="sm" color="white" />
              <Text color="white">Creating...</Text>
            </div>
          ) : (
            <Text color="white">Submit</Text>
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  );

  if (Access.allow_safety_documents !== 1) {
    return (
      <div role="alert" className="alert alert-warning w-1/2 mt-16">
        <svg xmlns="http://www.w3.org/2000/svg" className="stroke-current shrink-0 h-6 w-6" fill="white" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
        <span className="">Please subscribe to <b>Safety Management</b> feature</span>
      </div>
    );
  }

  return (
    <div className="px-2">
      {renderNewSafetyTypeModal()}

      {state.fetching && (
        <div className="flex justify-center mt6">
          <div>
            <Loading size='lg' />
            <div style={{ textAlign: "center" }}>Loading.....</div>
          </div>
        </div>
      )}

      <div className="space-y-6">
        {!state.fetching && state.documentTypeList.length !== 0 && (
          <div className="mt3 ml2 p-4 border-2 border-dotted rounded-xl shadow-lg">
            <div className="flex items-center justify-between mb-6 pb-4 border-b border-gray-100">
              <div>
                <h2 className="text-xl font-semibold text-gray-800">Safety Document Types</h2>
                <p className="text-sm text-gray-500 mt-1">Manage your safety document configurations</p>
              </div>
              <Button
                css={{br: 0}}
                auto
                color="primary"
                icon={<PaperPlus />}
                className="hover:bg-blue-600 transition-colors"
                onClick={() => setState(prev => ({
                  ...prev,
                  addDocument: !prev.addDocument,
                  editDocumentType: false,
                  deleteDocuments: false,
                }))}
              >
                <Text color="white">Create New Type</Text>
              </Button>
            </div>
            
            <div className="overflow-none">
              <table className="w-full flex flex-column mr2">
                <tr className="p-2 flex justify-around w-100 border-b border-gray-200">
                  <th className="flex flex-start items-center text-blue-gray-900 gap-2 font-bold opacity-70 w-25">
                    Document Types
                  </th>
                  <th className="flex justify-center items-center text-blue-gray-900 gap-2 font-bold opacity-70 w-25">
                    Active
                  </th>
                  <th className="flex justify-center items-center text-blue-gray-900 gap-2 font-bold opacity-70 w-25">
                    Auto Crop Image
                  </th>
                  <th className="flex justify-center items-center text-blue-gray-900 gap-2 font-bold opacity-70 w-25"></th>
                </tr>
                <tbody>
                  {state.documentTypeList.map((obj, idx) => renderUserRow(obj, idx))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {!state.fetching && state.documentTypeList.length === 0 && (
        <div>
          <div className="mt3 f5">No safety document type found.</div>
          <Button
            css={{br:0}}
            auto
            icon={<PaperPlus />}
            className="mt3"
            onClick={() => setState(prev => ({
              ...prev,
              addDocument: !prev.addDocument,
              editDocumentType: false,
              deleteDocuments: false,
            }))}
          >
            Create Safety Document Type
          </Button>
        </div>
      )}
    </div>
  );
};

export default SafetyDocumentType;